/**
 * App.jsx — Root router with UserAuthProvider
 * src/App.jsx
 *
 * Routes:
 *   /           → Home (user-facing)
 *   /login      → UserLogin
 *   /register   → UserRegister
 *   /admin      → AdminLayout (admin panel)
 *   /doctor     → DoctorLayout
 *   /assistant  → AssistantLayout
 *
 * Install:
 *   npm install bootstrap react-router-dom
 *   In main.jsx → import 'bootstrap/dist/css/bootstrap.min.css'
 */

import { Routes, Route, Navigate } from "react-router-dom";
import { UserAuthProvider } from "./context/UserAuthContext";

/* ── User-facing pages ── */
import Home from "./pages/user/Home";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import Doctors from "./pages/user/Doctors";
import Feedback from "./pages/user/Feedback";
import Gallery from "./pages/user/Gallery";
import Contact from "./pages/user/ContactUs";
import Services from "./pages/user/Services";

/* ── Private Routes ── */
import PrivateRoutes from "./routes/PrivateRoute";

/* ── Admin Login ── */
import CustomLogin from "./components/auth/CustomLogin";

/* ── Admin panel ── */
import AdminLayout from "./layouts/AdminLayout";
import AdminHome from "./pages/admin/AdminHome";
import ManageAdmins from "./pages/admin/ManageAdmins";
import ManageDoctors from "./pages/admin/ManageDoctors";
import ManageAssistants from "./pages/admin/ManageAssistants";
import ManageAppointments from "./pages/admin/ManageAppointments";
import ManagePatients from "./pages/admin/ManagePatients";
import ManageTreatments from "./pages/admin/ManageTreatments";
import Billing from "./pages/admin/Billing";
import Reports from "./pages/admin/Reports";
import Settings from "./pages/admin/Settings";
import ManageInventory from "./pages/admin/ManageInventory";
import ManageServices from "./pages/admin/ManageServices";


/* ── Assistant panel ── */
import AssistantLayout from "./layouts/AssistantLayout";
import AssistantHome from "./pages/assistant/AssistantHome";
import TodaysSchedule from "./pages/assistant/TodaysSchedule";
import AllAppointments from "./pages/assistant/AllAppointments";
import PatientsList from "./pages/assistant/PatientsList";
import PatientCheckin from "./pages/assistant/PatientCheckin";
import PatientFollowup from "./pages/assistant/PatientFollowup";
import Inventory from "./pages/assistant/Inventory";
import BillingAssist from "./pages/assistant/BillingAssist";
import DoctorSchedule from "./pages/assistant/DoctorSchedule";
import Messages from "./pages/assistant/Messages";
import Reminders from "./pages/assistant/Reminders";
import AssistantProfile from "./pages/assistant/AssistantProfile";
import AssistantSettings from "./pages/assistant/AssistantSettings";

/* ── Doctor panel ── */
import DoctorLayout from "./layouts/DoctorLayout";
import DoctorHome from "./pages/doctor/DoctorHome";
import DoctorAppointments from "./pages/doctor/TodayAppointments"; // Will create/move if needed
import ManageSchedule from "./pages/doctor/ManageSchedule";
import MyPatients from "./pages/doctor/MyPatients";
import PatientHistory from "./pages/doctor/PatientHistory";
import ManageOverview from "./pages/admin/ManageOverview";
import ManageSlides from "./pages/admin/ManageSlides";
import ManageGallery from "./pages/admin/ManageGallery";
import DoctorProfile from "./pages/doctor/DoctorProfile";
import CreateAppointment from "./pages/assistant/CreateAppointment";
function App() {
  return (
    /* UserAuthProvider wraps the entire app so any component can call useUserAuth() */
    <UserAuthProvider>
      {/* <BrowserRouter> */}
      <Routes>
        {/* ── USER FACING ── */}
        <Route path="/" element={<Home />} />
        <Route path="doctors" element={<Doctors />} />
        <Route path="services" element={<Services />} />
        <Route path="feedback" element={<Feedback />} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="Contact" element={<Contact />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />

        <Route path="/adminlogin" element={<CustomLogin />}></Route>
        <Route path="/assistantlogin" element={<CustomLogin />} />
        <Route path="/doctorlogin" element={<CustomLogin />} />
        {/* ── ADMIN PANEL ── */}
        <Route
          path="/admin"
          element={
            <PrivateRoutes role={"Admin"}>
              <AdminLayout />
            </PrivateRoutes>
          }
        >
          <Route index element={<AdminHome />} />
          <Route path="admins" element={<ManageAdmins />} />
          <Route path="doctors" element={<ManageDoctors />} />
          <Route path="assistants" element={<ManageAssistants />} />
          <Route path="appointments" element={<ManageAppointments />} />
          <Route path="patients" element={<ManagePatients />} />
          <Route path="treatments" element={<ManageTreatments />} />
          <Route path="billing" element={<Billing />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="inventory" element={<ManageInventory />} />
          <Route path="overview" element={<ManageOverview />} />
          <Route path="/admin/slides" element={<ManageSlides />} />
          <Route path="/admin/gallery" element={<ManageGallery />} />
          <Route path="/admin/services" element={<ManageServices />} />
        </Route>

        {/* ── ASSISTANT PANEL ── */}
        <Route
          path="/assistant"
          element={
            <PrivateRoutes role={"Assistant"}>
              <AssistantLayout />
            </PrivateRoutes>
          }
        >
          <Route index element={<AssistantHome />} />
          <Route path="schedule" element={<TodaysSchedule />} />
          <Route path="appointments" element={<AllAppointments />} />
          <Route path="patients" element={<PatientsList />} />
          <Route path="checkin" element={<PatientCheckin />} />
          <Route path="followup" element={<PatientFollowup />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="billing" element={<BillingAssist />} />
          <Route path="doctor-schedule" element={<DoctorSchedule />} />
          <Route path="messages" element={<Messages />} />
          <Route path="reminders" element={<Reminders />} />
          <Route path="profile" element={<AssistantProfile />} />
          <Route path="settings" element={<AssistantSettings />} />
          <Route path="create-appointment" element={<CreateAppointment />} />
        </Route>

        {/* ── DOCTOR PANEL ── */}
        <Route
          path="/doctor"
          element={
            <PrivateRoutes role={"Doctor"}>
              <DoctorLayout />
            </PrivateRoutes>
          }
        >
          <Route index element={<DoctorHome />} />
          <Route path="appointments" element={<DoctorAppointments />} />
          <Route path="schedule" element={<ManageSchedule />} />
          <Route path="patients" element={<MyPatients />} />
          <Route path="patients/:id" element={<PatientHistory />} />
          <Route path="drprofile" element={<DoctorProfile />} />
        </Route>
        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* </BrowserRouter> */}
    </UserAuthProvider>
  );
}

export default App;
